import { sortBy } from 'lodash-es';
import { createSelector } from 'reselect';
import { produce } from 'immer';
import bbox from '@turf/bbox';
import { feature, featureCollection } from '@turf/helpers';
import { ScopedType, ScopedAsyncType } from './scoped-type';
import { asyncAction } from './async-action';
import * as api from '../../lib/api';

const EMPTY_ARRAY = [];
const SLICE = 'fields';
const Type = ScopedType('FIELDS');
const AsyncType = ScopedAsyncType('FIELDS');

const LOAD = AsyncType('LOAD');
const AUTO_SELECT = Type('AUTO_SELECT');
const SELECT = Type('SELECT');
const FOCUS = Type('FOCUS');
const RESET = Type('RESET');

export const selectors = {
  hasAutoSelectedField: (state) => Boolean(state[SLICE]?.autoSelectedField),
  growerFields: (state) => state[SLICE]?.growerFields || EMPTY_ARRAY,
  selectedFields: (state) => state[SLICE]?.selectedGrowerFields || EMPTY_ARRAY,
  focusedAaid: (state) => state[SLICE]?.focusedAaid,

  selectedAaids: createSelector(
    (state) => state[SLICE]?.selectedGrowerFields,
    (fields) => fields?.map((f) => f.aaManagedId) || [],
  ),

  fieldBoundaries: createSelector(
    (state) => state[SLICE]?.growerFields,
    (fields) => buildBoundaries(fields),
  ),

  growerBounds: createSelector(
    (state) => state[SLICE]?.growerFields,
    (fields) => buildBoundingBox(fields),
  ),

  selectedBounds: createSelector(
    (state) => state[SLICE]?.growerFields,
    (state) => state[SLICE]?.selectedGrowerFields,
    (fields, selected) => {
      const aaids = selected?.map((f) => f.aaManagedId);
      return buildBoundingBox(
        fields?.filter((f) => aaids?.includes(f.aaManagedId)),
      );
    },
  ),

  focusedBounds: createSelector(
    (state) => state[SLICE]?.growerFields,
    (state) => state[SLICE]?.focusedAaid,
    (fields, focused) =>
      buildBoundingBox(fields?.filter((f) => focused === f.aaManagedId)),
  ),

  buildBounds: createSelector(
    (state) => state[SLICE]?.growerFields,
    (fields) => (selectedFields) =>
      buildBoundingBox(
        fields?.filter((f) =>
          selectedFields.some((sf) => sf.aaid === f.aaManagedId),
        ),
      ),
  ),
};

export const actions = {
  loadGrowerFields,
  setSelectedGrowerFields,
  focusAaid,
  reset,
  setAutoSelected,
};

function loadGrowerFields(growerId) {
  return asyncAction(LOAD, () => api.getFieldsByGrower(growerId));
}

function setSelectedGrowerFields(fields) {
  return { type: SELECT, payload: fields };
}

function focusAaid(aaid) {
  return { type: FOCUS, payload: aaid };
}

function reset() {
  return { type: RESET };
}

function setAutoSelected() {
  return { type: AUTO_SELECT };
}

const reducerHandlers = {
  [AUTO_SELECT]: reduceAutoSelected,
  [RESET]: reduceLoadFieldsBegin,
  [SELECT]: reduceSelectFields,
  [FOCUS]: reduceFocusField,
  [LOAD.start]: reduceLoadFieldsBegin,
  [LOAD.complete]: reduceLoadFieldsEnd,
  [LOAD.failure]: reduceLoadFieldsFailed,
};

function reduceLoadFieldsBegin(state) {
  state.isLoading = true;
  state.error = undefined;
  state.growerFields = [];
  state.selectedGrowerFields = [];
}

function reduceLoadFieldsEnd(state, fields) {
  state.isLoading = false;
  state.growerFields = sortBy(fields, 'fieldName');
}

function reduceLoadFieldsFailed(state, error) {
  state.isLoading = false;
  state.error = error;
}

function reduceSelectFields(state, selected) {
  state.selectedGrowerFields = selected;
}

function reduceAutoSelected(state) {
  state.autoSelectedField = true;
}

function reduceFocusField(state, focused) {
  state.focusedAaid = focused;
}

export const reducers = {
  [SLICE]: (
    state = { growerFields: [], selectedGrowerFields: [] },
    { type, payload, ...rest } = {},
  ) => {
    if (!reducerHandlers[type]) return state;
    return produce(state, (draft) =>
      reducerHandlers[type](draft, payload !== undefined ? payload : rest),
    );
  },
};

function buildBoundingBox(fields) {
  if (fields?.length) return bbox(buildBoundaries(fields));
}

function buildBoundaries(fields) {
  return featureCollection(
    fields.map((f) =>
      feature(JSON.parse(f.shape), {
        fieldName: f.fieldName,
        aaManagedId: f.aaManagedId,
        acres: f.acres,
      }),
    ),
  );
}
